import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import './BookingStepsPath.css'

export type BookingStepItem = {
  id: string | number
  label: string
  description?: string
}

export type BookingStepsPathProps = {
  title?: string
  steps: BookingStepItem[]
  /** 1-based active step for future booking wizard reuse */
  activeStep?: number
  className?: string
}

export function BookingStepsPath({
  title,
  steps,
  activeStep,
  className = '',
}: BookingStepsPathProps) {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLElement>(null)
  const [inView, setInView] = useState(false)
  const [mobileIndex, setMobileIndex] = useState(0)
  const pointerRef = useRef<{ id: number; x: number } | null>(null)

  const total = steps.length
  const current = Math.min(mobileIndex, Math.max(0, total - 1))
  const progressPct = total > 0 ? ((current + 1) / total) * 100 : 0

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true)
          io.disconnect()
        }
      },
      { threshold: 0.22 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const goMobile = useCallback(
    (i: number) => {
      if (total < 1) return
      setMobileIndex(((i % total) + total) % total)
    },
    [total],
  )

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    pointerRef.current = { id: e.pointerId, x: e.clientX }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerRef.current
    pointerRef.current = null
    if (!start || start.id !== e.pointerId) return
    const dx = e.clientX - start.x
    if (Math.abs(dx) < 48) return
    const rtl = document.documentElement.getAttribute('dir') === 'rtl'
    const next = rtl ? dx > 0 : dx < 0
    goMobile(current + (next ? 1 : -1))
  }

  return (
    <section
      ref={rootRef}
      className={`booking-steps${inView ? ' is-inview' : ''} ${className}`.trim()}
      aria-labelledby={title ? 'booking-steps-title' : undefined}
    >
      <div className="container booking-steps-inner">
        {title ? (
          <h2 id="booking-steps-title" className="booking-steps-title">
            {title}
          </h2>
        ) : null}

        {/* Desktop / tablet: horizontal path */}
        <ol className="booking-steps-track booking-steps-track--desktop">
          {steps.map((step, index) => {
            const n = index + 1
            const state =
              activeStep == null
                ? ''
                : n < activeStep
                  ? 'is-done'
                  : n === activeStep
                    ? 'is-active'
                    : 'is-todo'
            return (
              <li
                key={step.id}
                className={`booking-steps-item ${state}`.trim()}
                style={{ ['--step-i' as string]: index }}
              >
                <div className="booking-steps-node" aria-hidden={activeStep == null}>
                  <span>{n}</span>
                </div>
                <p className="booking-steps-label">{step.label}</p>
              </li>
            )
          })}
        </ol>

        {/* Mobile: progress + single step card */}
        <div className="booking-steps-mobile">
          <div className="booking-steps-mobile-meta">
            <span className="booking-steps-mobile-count">
              {String(current + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
            <div
              className="booking-steps-mobile-bar"
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={total}
              aria-valuenow={current + 1}
            >
              <span style={{ width: `${progressPct}%` }} />
            </div>
          </div>

          <div
            className="booking-steps-mobile-card"
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={() => {
              pointerRef.current = null
            }}
          >
            <p className="booking-steps-mobile-title">{steps[current]?.label}</p>
            {steps[current]?.description ? (
              <p className="booking-steps-mobile-desc">{steps[current].description}</p>
            ) : null}

            <button
              type="button"
              className="booking-steps-mobile-next"
              onClick={() => goMobile(current + 1)}
            >
              {current + 1 >= total ? t('hero.stepsAgain') : t('hero.stepsNext')}
              <span aria-hidden>→</span>
            </button>
          </div>

          <div className="booking-steps-mobile-pips" role="tablist" aria-label={title || 'Steps'}>
            {steps.map((step, i) => (
              <button
                key={step.id}
                type="button"
                role="tab"
                aria-selected={i === current}
                aria-label={`${i + 1}`}
                className={`booking-steps-mobile-pip${i === current ? ' is-active' : ''}`}
                onClick={() => goMobile(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
